import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Alert } from "react-bootstrap";

const CreateLocationForm = ({ onSuccess }) => {
  const [states, setStates] = useState([]);
  const [formData, setFormData] = useState({
    location_name: "",
    entity_type: "",
    address_line_1: "",
    city: "",
    state_id: "",
    postal_code: "",
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");

  const { user, tenant_id } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await secureAxios.post("/api/locations", {
        ...formData,
        address_line_2: "",
        created_by: user.user_id,
        tenant_id,
      });

      // Show success message using state instead of DOM manipulation
      setAlertMessage("Location created successfully!");
      setAlertVariant("success");
      setShowAlert(true);

      // Navigate after a short delay
      setTimeout(() => {
        navigate("/crm/locations");
        if (onSuccess) onSuccess();
      }, 1000);

      // Reset form
      setFormData({
        location_name: "",
        entity_type: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state_id: "",
        postal_code: "",
      });
    } catch (err) {
      console.error("Error creating location:", err);
      setAlertMessage("âŒ Failed to create location. Please try again.");
      setAlertVariant("danger");
      setShowAlert(true);
    }
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await secureAxios.get("/api/states");
        setStates(res.data);
      } catch (err) {
        console.error("Failed to fetch states:", err);
        setAlertMessage("Failed to load states. Please refresh the page.");
        setAlertVariant("warning");
        setShowAlert(true);
      }
    };

    fetchStates();
  }, []);

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  return (
    <div className="container mt-4">
      <h4>Create Location</h4>

      {showAlert && (
        <Alert
          variant={alertVariant}
          onClose={() => setShowAlert(false)}
          dismissible
        >
          {alertMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="location_name" className="form-label">
            Location Name <span className="text-danger">*</span>
          </label>
          <input
            id="location_name"
            name="location_name"
            className="form-control"
            value={formData.location_name}
            onChange={handleChange}
            required
            autoComplete="organization"
          />
        </div>
        <div className="mb-3">
          <label htmlFor="entity_type" className="form-label">Location Type</label>
          <input
            id="entity_type"
            name="entity_type"
            className="form-control"
            value={formData.entity_type}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="mb-3">
          <label htmlFor="address_line_1" className="form-label">
            Address Line 1 <span className="text-danger">*</span>
          </label>
          <input
            id="address_line_1"
            name="address_line_1"
            className="form-control"
            value={formData.address_line_1}
            onChange={handleChange}
            required
            autoComplete="address-line1"
          />
        </div>
        <div className="mb-3">
          <label htmlFor="city" className="form-label">
            City <span className="text-danger">*</span>
          </label>
          <input
            id="city"
            name="city"
            className="form-control"
            value={formData.city}
            onChange={handleChange}
            required
            autoComplete="address-level2"
          />
        </div>
        <div className="mb-3">
          <label htmlFor="state_id" className="form-label">
            State <span className="text-danger">*</span>
          </label>
          <select
            id="state_id"
            name="state_id"
            className="form-select"
            value={formData.state_id}
            onChange={handleChange}
            required
            autoComplete="address-level1"
          >
            <option value="">-- Select State --</option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_name} ({s.state_code})
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label htmlFor="postal_code" className="form-label">
            Postal Code <span className="text-danger">*</span>
          </label>
          <input
            id="postal_code"
            name="postal_code"
            className="form-control"
            value={formData.postal_code}
            onChange={handleChange}
            required
            autoComplete="postal-code"
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Save Location
        </button>
      </form>
    </div>
  );
};

export default CreateLocationForm;